import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit2, Pencil, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useIsMobile } from '@/hooks/use-mobile';
import { TimesheetCard } from '@/components/cards/TimesheetCard';
import { ManualEntryDialog } from '@/components/timesheets/ManualEntryDialog';
import { EditOwnTimesheetDialog } from '@/components/timesheets/EditOwnTimesheetDialog';
import { MonthYearPicker } from '@/components/ui/month-year-picker';
import { differenceInMinutes } from 'date-fns';
import { useCompanyTimezone } from '@/hooks/useCompanyTimezone';
import { formatEntryDate, formatEntryTime } from '@/lib/timesheetTime';
import { CompanyTimezoneHint } from '@/components/timesheets/CompanyTimezoneHint';
import { useMyTimeEntries } from '@/hooks/timesheets';
import { useHasPermission } from '@/hooks/permissions';

interface TimeEntry {
  id: string;
  clock_in: string;
  clock_out: string | null;
  entry_type?: 'automatic' | 'manual';
  status?: string;
}

const MyTimesheets = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const timezone = useCompanyTimezone();
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const { data: timeEntries = [], isLoading: loading, error, refetch } = useMyTimeEntries(selectedMonth);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  
  // Permission checks for self-service features
  const canSubmitManualEntries = useHasPermission('submit_unscheduled_timesheets');
  const canEditOwnTimesheet = useHasPermission('edit_own_timesheet');

  const formatDuration = (clockIn: string, clockOut: string | null) => {
    if (!clockOut) return 'In Progress';
    
    const minutes = differenceInMinutes(new Date(clockOut), new Date(clockIn));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes}m`;
    }
    
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  // Check if entry can be edited (not approved and has clock_out)
  const canEditEntry = (entry: TimeEntry) => {
    return canEditOwnTimesheet && 
           entry.status !== 'approved' && 
           entry.clock_out !== null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">My Timesheets</h1>
          <p className="text-muted-foreground">View your time tracking history</p>
        </div>
        {canSubmitManualEntries && (
          <Button onClick={() => setManualEntryOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Add Manual Entry
          </Button>
        )}
      </div>
      
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="space-y-1">
              <CardTitle>{t('timeAttendance.myTimesheets')}</CardTitle>
              <CompanyTimezoneHint />
            </div>
            <MonthYearPicker
              selectedDate={selectedMonth}
              onDateChange={setSelectedMonth}
              className="w-full sm:w-[200px]"
            />
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="h-10 w-10 text-destructive mb-3" />
              <h3 className="text-lg font-semibold mb-1">Failed to load time entries</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : timeEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No time entries found.
            </div>
          ) : isMobile ? (
            <div className="space-y-4">
              {timeEntries.map((entry) => (
                <div key={entry.id} className="relative">
                  <TimesheetCard entry={entry} />
                  {canEditEntry(entry as TimeEntry) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setEditEntry(entry as TimeEntry)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Total Hours</TableHead>
                  {canEditOwnTimesheet && <TableHead className="w-[80px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {entry.entry_type === 'manual' && (
                        <Badge variant="outline" className="text-xs">
                          <Edit2 className="h-3 w-3 mr-1" />
                          Manual
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatEntryDate(entry.clock_in, timezone)}
                    </TableCell>
                    <TableCell>
                      {formatEntryTime(entry.clock_in, timezone)}
                    </TableCell>
                    <TableCell>
                      {entry.clock_out 
                        ? formatEntryTime(entry.clock_out, timezone)
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      {formatDuration(entry.clock_in, entry.clock_out)}
                    </TableCell>
                    {canEditOwnTimesheet && (
                      <TableCell>
                        {canEditEntry(entry as TimeEntry) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditEntry(entry as TimeEntry)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ManualEntryDialog
        open={manualEntryOpen}
        onOpenChange={setManualEntryOpen}
      />

      <EditOwnTimesheetDialog
        open={!!editEntry}
        onOpenChange={(open) => { if (!open) setEditEntry(null); }}
        entry={editEntry}
      />
    </div>
  );
};

export default MyTimesheets;
